import appSettings from '@/app-settings.json';
import { GetRegionalPaymentStatisticsQuery } from '@/api/ariadne/__generated_types__/apollo';
import { TOP_LEVEL_TOT_TEST_IDS } from '@/top-level-tot/constants';
import { useRegionalPaymentStatistics } from '@/top-level-tot/hooks/use-regional-payment-statistics';
import { PaymentPeriodDateRange } from '@/top-level-tot/types';
import { calcPercentageLatePayments } from '@/top-level-tot/utils/utils';
import classNames from 'classnames';
import { Spinner } from 'deckard-ui/dist/components/Spinner/Spinner';
import { convertToCurrencyFormatFromCents } from 'deckard-ui/dist/utils/formatters';
import { Statistic } from './statistic';

// TODO: DECK-27285 Remove when tax estimations is ready
const taxEstimationsFeatureEnabled =
  appSettings?.uiConfigPayload?.taxEstimations;

type TaxSummary = GetRegionalPaymentStatisticsQuery['region']['taxSummary'];

function processRegionalStatistics(
  stats: TaxSummary | undefined
): Omit<TaxSummary, '__typename'> {
  if (!stats) {
    return {
      numLicensesIncomplete: 0,
      numLicensesPaid: 0,
      numLicensesPaidLate: 0,
      totalEstimatedTaxCents: 0,
      totalPenaltiesAndInterestPaidCents: 0,
      totalTaxPaidCents: 0,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __typename, ...rest } = stats;
  return rest;
}

type Props = {
  period: PaymentPeriodDateRange;
};

export function Summary(props: Props) {
  const { period } = props;

  const { regionalStatistics, isLoadingRegionalStatistics } =
    useRegionalPaymentStatistics({ period });

  const statistics = processRegionalStatistics(regionalStatistics);

  const totalTaxPaidCents: number = convertToCurrencyFormatFromCents(
    statistics.totalTaxPaidCents
  ) as number;

  const totalEstimatedTaxCents: number = convertToCurrencyFormatFromCents(
    // TODO: DECK-27285 Remove when tax estimations is ready
    taxEstimationsFeatureEnabled ? statistics.totalEstimatedTaxCents : 0
  ) as number;

  const totalPenaltiesAndInterestPaidCents: number =
    convertToCurrencyFormatFromCents(
      statistics.totalPenaltiesAndInterestPaidCents
    ) as number;

  const percentageLatePayments: string = calcPercentageLatePayments(
    statistics.numLicensesPaid,
    statistics.numLicensesPaidLate
  );

  return (
    <div
      data-testid={TOP_LEVEL_TOT_TEST_IDS.SUMMARY}
      className="max-w-2xl min-w-min"
    >
      <p>Summary</p>

      <div
        className={classNames(
          'border-[1px]',
          'border-[#999]',
          'rounded-md',
          'p-2',
          'min-w-[600px]',
          'min-h-[130px]',
          isLoadingRegionalStatistics && 'flex justify-center items-center',
          !isLoadingRegionalStatistics &&
            'gap-2 grid grid-cols-2 md:grid-cols-3'
        )}
      >
        {isLoadingRegionalStatistics ? (
          <Spinner />
        ) : (
          <>
            <Statistic
              testid={TOP_LEVEL_TOT_TEST_IDS.SUMMARY_TOTAL_TAX_PAID_FOR_PERIOD}
              label="Tax paid this period"
              value={totalTaxPaidCents}
            />

            <Statistic
              testid={TOP_LEVEL_TOT_TEST_IDS.SUMMARY_NUMBER_PAID_FOR_PERIOD}
              label="Permits completed"
              value={statistics.numLicensesPaid}
            />

            <Statistic
              testid={
                TOP_LEVEL_TOT_TEST_IDS.SUMMARY_PENALTIES_AND_INTEREST_PAID_FOR_PERIOD
              }
              label="Penalties and interest paid"
              value={totalPenaltiesAndInterestPaidCents}
            />

            <Statistic
              testid={
                TOP_LEVEL_TOT_TEST_IDS.SUMMARY_EXPECTED_TAX_TO_BE_PAID_FOR_PERIOD
              }
              label="Estimated tax this period*"
              value={totalEstimatedTaxCents}
            />

            <Statistic
              testid={TOP_LEVEL_TOT_TEST_IDS.SUMMARY_NUMBER_YET_TO_REPORT}
              label="Unreported or incomplete"
              value={statistics.numLicensesIncomplete}
            />

            <Statistic
              testid={TOP_LEVEL_TOT_TEST_IDS.SUMMARY_PERCENTAGE_LATE_PAYMENTS}
              label="Late payments"
              value={percentageLatePayments}
            />
          </>
        )}
      </div>
    </div>
  );
}
